import {
  removeInput,
  clearInputs,
  removeInputs,
  updateInputRecipients,
  updateInputDistribution,
  getInputs,
  clearInputFormErrors,
  createInput,
  updateInput,
  registerInputObserver,
  unregisterInputObserver,
  getBareInputs,
  enableInputs,
  disableInputs,
  rerouteInput,
} from '../actions/inputsActions'
import { logoutUser } from '../actions/userActions'
import { isOneOf } from '../actions'
import { EnrichedInput } from '../../api/nm-types'
import type { ApplicationException } from '../../api/ApplicationException'
import { createSlice } from '@reduxjs/toolkit'
import { Input } from 'common/api/v1/types'
import { createLoadingReducer } from './shared'

interface State {
  inputs: Array<EnrichedInput>
  total: number
  loading: boolean
  saving?: boolean
  rerouting: boolean
  dialogSaving: boolean
  formErrors?: ApplicationException['errorInfo']['details']
  inputsToObserve: { [inputId: string]: number }
  observedInputs: Array<Input>
}

const initialStateInputs: State = {
  inputs: [],
  total: 0,
  loading: false,
  rerouting: false,
  dialogSaving: false,
  inputsToObserve: {},
  observedInputs: [],
}

const { isLoadingAction, loadingReducer } = createLoadingReducer<State>(getInputs, removeInput, removeInputs)

const inputsSlice = createSlice({
  name: 'inputs',
  initialState: initialStateInputs,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getInputs.fulfilled,
        (state, { payload: { items: inputs, total } }): State => ({
          ...state,
          inputs,
          total,
        }),
      )
      .addCase(clearInputFormErrors, (state): State => ({ ...state, formErrors: undefined }))
      .addCase(clearInputs, (state): State => ({ ...state, inputs: [] }))
      .addCase(logoutUser.fulfilled, (): State => initialStateInputs)
      .addCase(
        registerInputObserver.pending,
        (
          state,
          {
            meta: {
              arg: { inputIds },
            },
          },
        ): State => {
          const inputsToObserve = { ...state.inputsToObserve }
          for (const inputId of inputIds) {
            inputsToObserve[inputId] = (inputsToObserve[inputId] || 0) + 1
          }
          return { ...state, inputsToObserve }
        },
      )
      .addCase(unregisterInputObserver, (state, { payload: { inputIds } }): State => {
        const newObservers = { ...state.inputsToObserve }
        for (const inputId of inputIds) {
          const numberOfObservers = (newObservers[inputId] || 0) - 1
          if (numberOfObservers < 1) {
            delete newObservers[inputId]
          } else {
            newObservers[inputId] = numberOfObservers
          }
        }

        const observedInputIds = Object.keys(newObservers)
        return {
          ...state,
          inputsToObserve: newObservers,
          observedInputs: state.observedInputs.filter((i) => observedInputIds.includes(i.id)),
        }
      })
      .addCase(getBareInputs.fulfilled, (state, { payload: bareInputs }): State => {
        const updateInputMetricsAndTsInfo = <T extends Input>(enrichedInput: T): T => {
          const bareInput = bareInputs.items.find((i) => i.id === enrichedInput.id)
          return bareInput
            ? { ...enrichedInput, metrics: bareInput.metrics, health: bareInput.health, tsInfo: bareInput.tsInfo }
            : enrichedInput
        }

        const observedInputIds = Object.keys(state.inputsToObserve)
        const observedInputs: Input[] = observedInputIds
          .map((id) => bareInputs.items.find((i) => i.id === id) as Input)
          .filter(Boolean)

        return {
          ...state,
          inputs: state.inputs.map(updateInputMetricsAndTsInfo),
          observedInputs,
        }
      })
      .addCase(enableInputs.fulfilled, (state, { payload: enableInputs }): State => {
        const enableInput = (input: Input): Input => {
          const enabledInput = enableInputs.find((i) => i.id === input.id)
          if (enabledInput) {
            return { ...input, adminStatus: enabledInput.adminStatus }
          }
          return input
        }

        return {
          ...state,
          inputs: state.inputs.map(enableInput),
          observedInputs: state.observedInputs.map(enableInput),
        }
      })
      .addCase(disableInputs.fulfilled, (state, { payload: disableInputs }): State => {
        const disableInput = (input: Input): Input => {
          const disabledInput = disableInputs.find((i) => i.id === input.id)
          if (disabledInput) {
            return { ...input, adminStatus: disabledInput.adminStatus }
          }
          return input
        }

        return {
          ...state,
          inputs: state.inputs.map(disableInput),
          observedInputs: state.observedInputs.map(disableInput),
        }
      })
      .addMatcher(
        isOneOf([
          updateInputDistribution.fulfilled,
          updateInputDistribution.rejected,
          updateInputRecipients.fulfilled,
          updateInputRecipients.rejected,
        ]),
        (state): State => ({ ...state, dialogSaving: false }),
      )
      .addMatcher(
        isOneOf([getInputs.pending, removeInput.pending, removeInputs.pending]),
        (state): State => ({ ...state, formErrors: undefined }),
      )
      .addMatcher(
        isOneOf([createInput.pending, updateInput.pending]),
        (state): State => ({ ...state, saving: true, formErrors: undefined }),
      )
      .addMatcher(
        isOneOf([updateInputDistribution.pending, updateInputRecipients.pending]),
        (state): State => ({ ...state, dialogSaving: true }),
      )
      .addMatcher(isOneOf([rerouteInput.pending]), (state) => ({
        ...state,
        rerouting: true,
      }))
      .addMatcher(isOneOf([rerouteInput.fulfilled, rerouteInput.rejected]), (state) => ({
        ...state,
        rerouting: false,
      }))
      .addMatcher(isOneOf([createInput.fulfilled, updateInput.fulfilled]), (state) => ({
        ...state,
        saving: undefined,
      }))
      .addMatcher(isOneOf([createInput.rejected, updateInput.rejected]), (state, { payload }) => ({
        ...state,
        saving: false,
        formErrors: payload?.details,
      }))
      .addMatcher(isLoadingAction, loadingReducer)
  },
})

export default inputsSlice.reducer

export const selectInputs = ({ inputsReducer }: { inputsReducer: State }) => ({
  inputs: inputsReducer.inputs,
  total: inputsReducer.total,
})
